import React, { useState, useEffect } from 'react';
import { FileText, Upload, Trash2, Download, Calendar, User } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const OrderInvoices = ({ orderId, adminToken }) => {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    file: null,
    type: 'invoice',
    invoiceNumber: '',
    notes: ''
  });

  useEffect(() => {
    if (orderId && adminToken) {
      loadInvoices();
    }
  }, [orderId, adminToken]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${orderId}/invoices`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setInvoices(data.invoices || []);
        }
      }
    } catch (error) {
      console.error('Errore caricamento fatture:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast({
          title: "Errore",
          description: "Solo file PDF sono ammessi",
          variant: "destructive"
        });
        return;
      }
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      toast({
        title: "Errore",
        description: "Seleziona un file PDF",
        variant: "destructive"
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('invoice_type', uploadData.type);
      if (uploadData.invoiceNumber) {
        formData.append('invoice_number', uploadData.invoiceNumber);
      }
      if (uploadData.notes) {
        formData.append('notes', uploadData.notes);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${orderId}/invoices/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          },
          body: formData
        }
      );

      if (response.ok) {
        toast({
          title: "Documento caricato!",
          description: "Il documento è stato caricato con successo"
        });
        setShowUploadModal(false);
        setUploadData({
          file: null,
          type: 'invoice',
          invoiceNumber: '',
          notes: ''
        });
        loadInvoices();
      } else {
        const error = await response.json();
        toast({
          title: "Errore",
          description: error.detail || "Errore durante il caricamento",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Errore upload:', error);
      toast({
        title: "Errore",
        description: "Errore durante il caricamento",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!confirm('Sei sicuro di voler eliminare questo documento?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${orderId}/invoices/${invoiceId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Documento eliminato",
          description: "Il documento è stato eliminato con successo"
        });
        loadInvoices();
      } else {
        toast({
          title: "Errore",
          description: "Errore durante l'eliminazione",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Errore eliminazione:', error);
      toast({
        title: "Errore",
        description: "Errore durante l'eliminazione",
        variant: "destructive"
      });
    }
  };

  const getDocumentTypeLabel = (type) => {
    return type === 'invoice' ? 'Fattura' : 'Ricevuta';
  };

  if (loading) {
    return <div className="text-center py-4">Caricamento...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Fatture e Ricevute</h3>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          <Upload className="h-4 w-4" />
          <span>Carica Documento</span>
        </button>
      </div>

      {invoices.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-2 text-gray-400" />
          <p>Nessun documento caricato</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center space-x-4">
                <FileText className="h-8 w-8 text-red-500" />
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{invoice.fileName}</span>
                    <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">
                      {getDocumentTypeLabel(invoice.type)}
                    </span>
                  </div>
                  {invoice.invoiceNumber && (
                    <p className="text-sm text-gray-600">N° {invoice.invoiceNumber}</p>
                  )}
                  {invoice.notes && (
                    <p className="text-sm text-gray-500 italic">{invoice.notes}</p>
                  )}
                  <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                    <span className="flex items-center space-x-1">
                      <User className="h-3 w-3" />
                      <span>{invoice.uploadedBy}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(invoice.uploadedAt).toLocaleDateString('it-IT')}</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/upload/s3/${invoice.s3Key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  title="Scarica"
                >
                  <Download className="h-5 w-5" />
                </a>
                <button
                  onClick={() => handleDelete(invoice.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                  title="Elimina"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Carica Documento</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo Documento *
                </label>
                <select
                  value={uploadData.type}
                  onChange={(e) => setUploadData({ ...uploadData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="invoice">Fattura</option>
                  <option value="receipt">Ricevuta</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File PDF *
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                {uploadData.file && (
                  <p className="mt-1 text-sm text-gray-600">{uploadData.file.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numero Documento
                </label>
                <input
                  type="text"
                  value={uploadData.invoiceNumber}
                  onChange={(e) => setUploadData({ ...uploadData, invoiceNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Es: FT-2025-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={uploadData.notes}
                  onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Note aggiuntive (opzionale)"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadData({
                    file: null,
                    type: 'invoice',
                    invoiceNumber: '',
                    notes: ''
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={uploading}
              >
                Annulla
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {uploading ? 'Caricamento...' : 'Carica'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderInvoices;
